import { computed, Injectable, signal } from "@angular/core";
import { GroupFilterState } from "../../features/crm/groups/types/filter.types";
import { groupClassTypes } from "../../core/utils/subject-group.util";
import { splitUniversityAudience } from "../../core/utils/group-label.util";

const INITIAL_FILTER_STATE: GroupFilterState = {
    selectedStatusFilter: [],
    selectedOnlineFilter: [],
    selectedClassTypeFilter: [],
    selectedModalityFilter: [],
    selectedUniversityFilter: [],
    selectedAcademyFilter: [],
    selectedQuarterFilter: [],
    subjectSearchFilter: '',
    letterSearchFilter: '',
    selectedTeacherFilter: [],
    priceSearchFilter: '',
    revenueSearchFilter: '',
    paidSearchFilter: '',
    pendingBalanceSearchFilter: '',
    honorariumSearchFilter: '',
    completedClassesSearchFilter: '',
    phaseSearchFilter: '',
    daysInPhaseSearchFilter: '',
    sessionSearchFilter: '',
    sortBy: undefined,
    sortOrder: undefined
};

@Injectable({
    providedIn: 'root'
})
export class SharedStore {
    
    readonly groupFilterState = signal<GroupFilterState>({ ...INITIAL_FILTER_STATE });

    readonly unifiedGroupFilter = computed(() => {
        const state = this.groupFilterState();
        const filter: Record<string, any> = {};

        if (state.selectedStatusFilter.length > 0) {
            filter['states'] = state.selectedStatusFilter;
        }

        if (state.selectedQuarterFilter.length > 0) {
            filter['quarter'] = state.selectedQuarterFilter;
        }

        if (state.selectedOnlineFilter.length === 1) {
            filter['isOnline'] = state.selectedOnlineFilter[0];
        }

        if (state.selectedModalityFilter.length > 0) {
            filter['modalities'] = state.selectedModalityFilter;
        }

        const classTypes = groupClassTypes(state.selectedClassTypeFilter);
        if (classTypes.length === 1) {
            filter['isFixedRate'] = classTypes.includes('Curso');
        }

        const { universities, audiences } = splitUniversityAudience(state.selectedUniversityFilter);

        if (universities.length > 0) {
            filter['universityId'] = universities;
        }

        if (audiences.length > 0) {
            filter['audiences'] = audiences;
        }

        if (state.selectedAcademyFilter.length > 0) {
            filter['academies'] = state.selectedAcademyFilter;
        }

        if (state.subjectSearchFilter.trim()) {
            filter['subjectName'] = state.subjectSearchFilter;
        }

        if (state.letterSearchFilter.trim()) {
            filter['letter'] = state.letterSearchFilter;
        }

        if (state.selectedTeacherFilter.length > 0) {
            filter['teacherId'] = state.selectedTeacherFilter;
        }

        if (state.priceSearchFilter.trim()) {
            filter['price'] = parseFloat(state.priceSearchFilter.replace(',', '.'));
        }

        if (state.revenueSearchFilter.trim()) {
            filter['totalRevenue'] = parseFloat(state.revenueSearchFilter.replace(',', '.'));
        }

        if (state.paidSearchFilter.trim()) {
            filter['totalPaid'] = parseFloat(state.paidSearchFilter.replace(',', '.'));
        }

        if (state.pendingBalanceSearchFilter.trim()) {
            filter['pendingBalance'] = parseFloat(state.pendingBalanceSearchFilter.replace(',', '.'));
        }

        if (state.honorariumSearchFilter.trim()) {
            filter['teacherHonorarium'] = parseFloat(state.honorariumSearchFilter.replace(',', '.'));
        }

        if (state.completedClassesSearchFilter && state.completedClassesSearchFilter.trim()) {
            const numValue = parseFloat(String(state.completedClassesSearchFilter).replace(',', '.'));
            if (!isNaN(numValue)) {
                filter['completedClasses'] = numValue;
            }
        }

        if (state.phaseSearchFilter && state.phaseSearchFilter.trim()) {
            const numValue = parseInt(String(state.phaseSearchFilter));
            if (!isNaN(numValue)) {
                filter['phase'] = numValue;
            }
        }

        if (state.daysInPhaseSearchFilter && state.daysInPhaseSearchFilter.trim()) {
            const numValue = parseInt(String(state.daysInPhaseSearchFilter));
            if (!isNaN(numValue)) {
                filter['daysInPhase'] = numValue;
            }
        }

        if (state.sessionSearchFilter && state.sessionSearchFilter.trim()) {
            filter['sessionSearch'] = state.sessionSearchFilter.trim();
        }

        if (state.sortBy) {
            filter['sortBy'] = state.sortBy;
        }

        if (state.sortOrder) {
            filter['sortOrder'] = state.sortOrder;
        }

        return filter;
    });

    updateGroupFilterState(updates: Partial<GroupFilterState>): void {
        const currentState = this.groupFilterState();
        this.groupFilterState.set({ ...currentState, ...updates });
    }

    clearGroupFilters(): void {
        this.groupFilterState.set({ ...INITIAL_FILTER_STATE });
    }
}

